import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Button } from '@clef/client-library';
import { ProjectFolder } from '@clef/shared/types';
import {
  useDeleteProjectFolderMutation,
  useGetAllProjectFoldersQuery,
} from '@/serverStore/projectFolders';
import { useGetProjectsQuery } from '@/serverStore/projects';

const useStyles = makeStyles(theme => ({
  deleteBtn: {
    fontSize: '14px',
    marginTop: 5,
    marginBottom: 20,
  },
  keepFolderBtn: {
    fontSize: 14,
    marginTop: 5,
    marginBottom: 20,
  },
  deleteText: {
    fontWeight: 'bold',
    color: theme.palette.secondary.main,
  },
  dialogContent: {
    marginLeft: 10,
    marginTop: 10,
    marginBottom: 20,
  },
  errorText: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.main,
  },
}));

interface DeleteFolderDialogProps {
  folder: ProjectFolder | null;
  open: boolean;
  onClose: () => void;
}

export const DeleteFolderDialog: React.FC<DeleteFolderDialogProps> = ({
  folder,
  open,
  onClose,
}) => {
  const styles = useStyles();
  const [error, setError] = useState<string | null>(null);
  const deleteFolderMutation = useDeleteProjectFolderMutation();
  const { data: allFolders } = useGetAllProjectFoldersQuery();
  const { data: allProjects } = useGetProjectsQuery();

  // Check if folder has items inside (frontend validation)
  const folderHasItems = useMemo(() => {
    if (!folder || !allFolders || !allProjects) return false;

    const hasSubfolders = allFolders.some(f => f.parentFolderId === folder.id);
    const hasProjects = allProjects.some(p => p.projectFolderId === folder.id);

    return hasSubfolders || hasProjects;
  }, [folder, allFolders, allProjects]);

  // Reset error when dialog opens/closes
  useEffect(() => {
    if (open) {
      // Show error immediately if folder has items
      if (folderHasItems) {
        setError(
          t('This folder is not empty. Please move or delete all projects and subfolders first.'),
        );
      } else {
        setError(null);
      }
    }
  }, [open, folderHasItems]);

  const handleDelete = async () => {
    if (!folder) {
      onClose();
      return;
    }

    setError(null);

    try {
      await deleteFolderMutation.mutateAsync(folder.id);
      onClose();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message: unknown }).message)
          : t('Failed to delete folder');

      // Check if it's a "folder not empty" error
      if (
        errorMessage.toLowerCase().includes('not empty') ||
        errorMessage.toLowerCase().includes('has subfolders') ||
        errorMessage.toLowerCase().includes('has projects')
      ) {
        setError(
          t('This folder is not empty. Please move or delete all projects and subfolders first.'),
        );
      } else {
        setError(errorMessage);
      }
    }
  };

  if (!folder) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} data-testid="delete-folder-dialog">
      <DialogContent>
        <Box className={styles.dialogContent}>
          <Typography>
            {t('Are you sure you want to {{delete}} folder {{folderName}}?', {
              delete: <span className={styles.deleteText}>{t('delete')}</span>,
              folderName: <strong>{folder.name}</strong>,
            })}
          </Typography>
          {error && (
            <Typography className={styles.errorText} variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleDelete}
          className={styles.deleteBtn}
          id="confirm-folder-deletion"
          disabled={deleteFolderMutation.isLoading || folderHasItems}
        >
          {deleteFolderMutation.isLoading ? t('Deleting...') : t('Yes, Delete Folder')}
        </Button>
        <Button
          variant="outlined"
          color="default"
          onClick={onClose}
          className={styles.keepFolderBtn}
          id="cancel-folder-deletion"
          disabled={deleteFolderMutation.isLoading}
        >
          {t('No, Keep Folder')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteFolderDialog;
